'use client'

import * as React from 'react'
import { XIcon } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { Badge } from '@/shared/components/ui/badge'
import { Button } from '@/shared/components/ui/button'
import { Checkbox } from '@/shared/components/ui/checkbox-native'
import {
	Command,
	CommandEmpty,
	CommandGroup,
	CommandItem,
	CommandList,
	CommandSeparator,
} from '@/shared/components/ui/command'
import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from '@/shared/components/ui/popover'
import { Separator } from '@/shared/components/ui/separator'
import { Tag } from '@/shared/components/ui/tag'
import {
	Virtualized,
	VirtualizedVirtualizer,
} from '@/shared/components/ui/virtualizer'
import { cn } from '@/shared/lib/utils'

import { getAnchorStatusChoices } from '../../lib/constants'

type ApprovalTableStatusFilterProps = {
	onChange: (value?: string[]) => void
	value?: string[]
	title: string
}
type Option = {
	value: string
	label: string
	variant: string
	icon: React.ReactNode
}

export function ApprovalTableStatusFilter({
	onChange,
	value,
	title,
}: ApprovalTableStatusFilterProps) {
	const { t } = useTranslation()
	const [open, setOpen] = React.useState(false)

	const selectedValues = React.useMemo(() => new Set(value), [value])
	const options = React.useMemo(() => getAnchorStatusChoices(t), [t])

	const onItemSelect = React.useCallback(
		(option: Option, isSelected: boolean) => {
			const newSelectedValues = new Set(selectedValues)
			if (isSelected) {
				newSelectedValues.delete(option.value)
			} else {
				newSelectedValues.add(option.value)
			}
			const filterValues = Array.from(newSelectedValues)
			onChange(filterValues.length ? filterValues : undefined)
		},
		[selectedValues, onChange]
	)

	const onReset = React.useCallback(
		(event?: React.MouseEvent) => {
			event?.stopPropagation()
			onChange(undefined)
		},
		[onChange]
	)

	return (
		<Popover open={open} onOpenChange={setOpen}>
			<PopoverTrigger asChild>
				<Button
					variant='outline'
					className={cn('group/state-button relative border-dashed', {
						'hover:pr-6': selectedValues?.size > 0,
					})}
				>
					{title}
					{selectedValues?.size > 0 && (
						<>
							<Separator
								orientation='vertical'
								className='mx-0.5 data-[orientation=vertical]:h-4'
							/>
							<Tag className='lg:hidden'>{selectedValues.size}</Tag>
							<div className='hidden items-center gap-1 lg:flex'>
								{selectedValues.size > 3 ? (
									<Badge className='block max-w-30 shrink-0 grow-0 truncate'>
										{selectedValues.size}
									</Badge>
								) : (
									options
										.filter((option) => selectedValues.has(option.value))
										.map((option) => (
											<Badge
												className='block max-w-30 shrink-0 grow-0 truncate'
												key={option.value as string}
											>
												{option.label}
											</Badge>
										))
								)}
							</div>
						</>
					)}
					{selectedValues?.size > 0 ? (
						<div
							role='button'
							aria-label={`Clear ${title} filter`}
							tabIndex={0}
							onClick={onReset}
							className='absolute top-1.75 right-1 hidden rounded-sm opacity-70 transition-opacity group-hover/state-button:inline-flex hover:opacity-100 focus-visible:ring-1 focus-visible:ring-ring/30 focus-visible:outline-none'
						>
							<XIcon />
						</div>
					) : null}
				</Button>
			</PopoverTrigger>
			<PopoverContent
				className='w-auto max-w-96 min-w-[max(var(--radix-popover-trigger-width),300px)] p-0'
				align='start'
			>
				<FilterContent
					options={options}
					selectedValues={selectedValues}
					onItemSelect={onItemSelect}
					onReset={onReset}
				/>
			</PopoverContent>
		</Popover>
	)
}

const FilterContent = ({
	options,
	selectedValues,
	onItemSelect,
	onReset,
}: {
	options: Option[]
	selectedValues: Set<string>
	onItemSelect: (option: Option, isSelected: boolean) => void
	onReset: () => void
}) => {
	const { t } = useTranslation()

	return (
		<Command shouldFilter={false} className='p-0'>
			<Virtualized asChild>
				<CommandList className='max-h-75 scroll-pb-12'>
					<CommandEmpty>{t('noResultsFound')}</CommandEmpty>
					<CommandGroup>
						<VirtualizedVirtualizer>
							{options.map((option) => {
								const isSelected = selectedValues.has(option.value as string)

								return (
									<CommandItem
										key={option.value as string}
										onSelect={() => onItemSelect(option, isSelected)}
									>
										<Checkbox checked={isSelected} />
										<span>{option.label}</span>
									</CommandItem>
								)
							})}
						</VirtualizedVirtualizer>
					</CommandGroup>
					{selectedValues.size > 0 && (
						<>
							<CommandSeparator className='sticky bottom-10' />
							<CommandGroup className='sticky bottom-0 h-10 bg-popover'>
								<CommandItem
									onSelect={() => onReset()}
									className='justify-center text-center'
								>
									{t('general_clear')}
								</CommandItem>
							</CommandGroup>
						</>
					)}
				</CommandList>
			</Virtualized>
		</Command>
	)
}
